import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

// Phase 6: agent-queryability shared queries for messages mentioning a
// specific user. Joins via the `message_mentions` junction, which is
// populated solely by application code as of Phase 7c-C (migration 117
// dropped the legacy `sync_message_mentions_from_jsonb` trigger). See
// `message-builders.ts` for the INSERT path and the
// `syncMessageMentionsJunction` helper for the UPDATE path.
//
// Performance: backed by migration 113's composite index
// `idx_message_mentions_user_id_message_id ON message_mentions(user_id, message_id)`
// so `forUser` is an indexed seek, not a table scan.
//
// SECURITY: outer channelMemberGuard ensures we only return messages
// from channels the caller is an active member of. Without it, an
// attacker could @-mention an outside user from a private channel and
// leak the mention via this query.
//
// NOTE (no current consumers): scaffolding for future agent paths.
// Today the agent surface uses the server-side `messagesMentioning`
// helper in mcp-operations.ts which benefits directly from migration
// 106's `(user_id, message_id)` index via Postgres.

export const messageMentionsQueries = {
  // Messages mentioning `userId`, recent-first.
  forUser: defineQuery(
    z.object({
      userId: z.string(),
      since: z.number().optional(),
      limit: z.number().default(100),
    }),
    ({ args: { userId, since, limit }, ctx: { sub } }) => {
      const base = zql.messages
        .whereExists("message_mentions", (mmq) => mmq.where("user_id", userId))
        .whereExists("channel", channelMemberGuard(sub));
      const filtered = since ? base.where("created_at", ">=", since) : base;
      return filtered
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit);
    },
  ),
  // Messages mentioning `userId` in a specific channel.
  forUserInChannel: defineQuery(
    z.object({
      userId: z.string(),
      channelId: z.string(),
      limit: z.number().default(100),
    }),
    ({ args: { userId, channelId, limit }, ctx: { sub } }) =>
      zql.messages
        .where("channel_id", channelId)
        .whereExists("message_mentions", (mmq) => mmq.where("user_id", userId))
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit),
  ),
};
